import {
  type ColorValue,
  type NumericValue,
  type PropertyTypeValueMap,
  StyleType,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createStylesParams,
  type ISettingsColor,
  type ISettingsFont,
  type ISiteTextPreset,
  type IStyleParam,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import { CustomCssVarsFn } from '@wix/yoshi-flow-editor';
import { AlignmentOptions, DisplayStyles } from '../../types/types';

export type IStylesParams = IStyleParamsBoolean &
  IStylesParamsNumbers &
  IStyleParamsColors &
  IStyleParamsFonts;

export type IStyleParamsBoolean = {
  headerTitleVisibility: StyleParamType.Boolean;
  headerServiceFilterVisibility: StyleParamType.Boolean;
  headerStaffFilterVisibility: StyleParamType.Boolean;
  headerLocationFilterVisibility: StyleParamType.Boolean;
  headerTimezoneSelectionVisibility: StyleParamType.Boolean;
  tableDayDividerVisibility: StyleParamType.Boolean;
  tableSessionDividerVisibility: StyleParamType.Boolean;
  sessionServiceImageVisibility: StyleParamType.Boolean;
  sessionDurationVisibility: StyleParamType.Boolean;
  sessionStaffImageVisibility: StyleParamType.Boolean;
  sessionLocationVisibility: StyleParamType.Boolean;
  sessionStaffMemberVisibility: StyleParamType.Boolean;
  sessionPriceVisibility: StyleParamType.Boolean;
  sessionNumberOfSpotsLeftVisibility: StyleParamType.Boolean;
  canBookFromDailyAgenda: StyleParamType.Boolean;
};

export type IStyleParamsFonts = {
  headerTitleFont: StyleParamType.Font;
  datePickerDatesFont: StyleParamType.Font;
  filtersFont: StyleParamType.Font;
  tableDatesFont: StyleParamType.Font;
  tableDayFont: StyleParamType.Font;
  sessionsItemsServiceNameFont: StyleParamType.Font;
  sessionsItemsPrimaryFont: StyleParamType.Font;
  sessionsItemsSecondaryFont: StyleParamType.Font;
  sessionItemsRegularButtonFont: StyleParamType.Font;
  loadMoreButtonFont: StyleParamType.Font;
};

export type IStylesParamsNumbers = {
  generalBorderWidth: StyleParamType.Number;
  generalWidgetCornerRadius: StyleParamType.Number;
  tableDayDividerWidth: StyleParamType.Number;
  tableSessionDividerWidth: StyleParamType.Number;
  sessionItemsCornerRadius: StyleParamType.Number;
  sessionItemsImageCornerRadius: StyleParamType.Number;
  sessionsItemsRegularButtonBorderWidth: StyleParamType.Number;
  sessionsItemsRegularCornerRadius: StyleParamType.Number;
  loadMoreButtonBorderWidth: StyleParamType.Number;
  loadMoreButtonCornerRadius: StyleParamType.Number;
  layoutHeaderSidePadding: StyleParamType.Number;
  layoutHeaderTopPadding: StyleParamType.Number;
  layoutHeaderBottomPadding: StyleParamType.Number;
  layoutImageSize: StyleParamType.Number;
  itemsLayoutTopAndBottomPadding: StyleParamType.Number;
  dayLayoutTopAndBottomPadding: StyleParamType.Number;
  dayLayoutsidePadding: StyleParamType.Number;
  dayLayoutpaddingBetweenSessionsAndDates: StyleParamType.Number;
};

export type IStyleParamsColors = {
  headerTitleColor: StyleParamType.Color;
  datePickerDatesColor: StyleParamType.Color;
  selectionAndHighlightColor: StyleParamType.Color;
  datePickerSelectedColor: StyleParamType.Color;
  datePickerDisabledColor: StyleParamType.Color;
  filtersColor: StyleParamType.Color;
  tableDateColor: StyleParamType.Color;
  tableDayColor: StyleParamType.Color;
  tableDayDividerOpacity: StyleParamType.Color;
  tableMobileDayDividerColor: StyleParamType.Color;
  generalBorderOpacityAndColor: StyleParamType.Color;
  tableSessionDividerOpacity: StyleParamType.Color;
  generalWidgetBackgroundColor: StyleParamType.Color;
  sessionItemsBackgroundColor: StyleParamType.Color;
  sessionItemsImageOverlayOpacityAndColor: StyleParamType.Color;
  sessionsItemsButtonBorderAndOpacityColor: StyleParamType.Color;
  sessionsItemsDisabledButtonBorderAndOpacityColor: StyleParamType.Color;
  sessionsItemsButtonBackgroundColor: StyleParamType.Color;
  sessionsItemsDisabledButtonBackgroundColor: StyleParamType.Color;
  sessionsItemsTextColor: StyleParamType.Color;
  sessionsItemsDisabledTextColor: StyleParamType.Color;
  sessionItemsRegularButtonColor: StyleParamType.Color;
  sessionsItemsRegularButtonBackgroundAndOpacityColor: StyleParamType.Color;
  sessionsItemsRegularButtonBorderAndOpacityColor: StyleParamType.Color;
  loadMoreButtonColor: StyleParamType.Color;
  loadMoreButtonBackgroundAndOpacityColor: StyleParamType.Color;
  loadMoreButtonBorderAndOpacityColor: StyleParamType.Color;
  sessionItemsPrimaryTextColor: StyleParamType.Color;
  sessionItemsServiceNameTextColor: StyleParamType.Color;
  sessionItemsSecondaryTextColor: StyleParamType.Color;
};

export type ICustomStylesParam = {
  agendaAlignedItem: string;
  displayHeaderTitle: string;
  displayHeaderServiceFilter: string;
  displayHeaderStaffFilter: string;
  displayHeaderLocationFilter: string;
  displayHeaderTimezoneSelection: string;
  displaySessionServiceImage: string;
  displaySessionDuration: string;
  displaySessionStaffImage: string;
  displaySessionLocation: string;
  displaySessionStaffMember: string;
  displaySessionPrice: string;
  displaySessionNumberOfSpotsLeft: string;
  displayTableDayDivider: string;
  displayTableSessionDivider: string;
  displayBookFromDailyAgenda: string;
};

export type ICustomStyleParamsInput = {
  strings: never;
  colors: { [key in keyof IStyleParamsColors]: string };
  numbers: { [key in keyof IStylesParamsNumbers]: number };
  booleans: { [key in keyof IStyleParamsBoolean]: boolean };
  fonts: { [key in keyof IStyleParamsFonts]: string };
};

type BooleanStyleParam = Omit<
  IStyleParam<StyleParamType.Boolean, boolean>,
  'name'
>;

const booleanParam = (defaultValue: boolean): BooleanStyleParam => ({
  type: StyleParamType.Boolean,
  getDefaultValue: () => defaultValue,
  metadata: {
    type: StyleType.UNKNOWN_CssPropertyType,
    defaultValue: {
      type: 'static',
      value: defaultValue,
    },
  },
});

type NumberStyleParam = Omit<
  IStyleParam<StyleParamType.Number, number>,
  'name'
>;

type StyleTypeFor<ValueType> = keyof {
  [K in keyof PropertyTypeValueMap as Required<
    PropertyTypeValueMap[K]
  > extends Required<ValueType>
    ? K
    : never]: K;
};

const numberParam = (
  type: StyleTypeFor<NumericValue>,
  defaultValue: number | undefined,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: () => defaultValue as number,
  metadata: {
    type: type as StyleType.UNKNOWN_CssPropertyType, // type assertion is needed to avoid strange TypeScript error
    defaultValue: {
      type: 'static',
      value: defaultValue as number,
    },
  },
});

type ColorStyleParam = Omit<
  IStyleParam<StyleParamType.Color, ISettingsColor>,
  'name'
>;

const colorParam = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset),
  metadata: {
    type,
    defaultValue: {
      type: 'static',
      preset,
    },
  },
});

const colorParamWithOpacity = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  opacity: number,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset, opacity),
  metadata: {
    type,
    defaultValue: {
      type: 'static',
      preset,
      opacity,
    },
  },
});

type FontStyleParam = Omit<
  IStyleParam<StyleParamType.Font, ISettingsFont>,
  'name'
>;

const fontParam = (
  preset: keyof ISiteTextPreset,
  props: { size?: number; htmlTag?: string },
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam(preset, props),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset,
      ...props,
    },
  },
});

const fontParamWithMobileDefault = (
  preset: keyof ISiteTextPreset,
  mobileSize: number,
  size: number,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: ({
    textPresets,
    isMobile,
  }: {
    textPresets: ISiteTextPreset;
    isMobile: boolean;
  }) =>
    wixFontParam(preset, { size: isMobile ? mobileSize : size })({
      textPresets,
    }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset,
      size,
    },
    mobileDefaultValue: {
      type: 'static',
      preset,
      size: mobileSize,
    },
  },
});

export const styleParamsDefinitions: {
  [key in keyof IStylesParams]: IStyleParam;
} = {
  generalBorderWidth: numberParam(StyleType.borderWidth, 0),
  loadMoreButtonBorderWidth: numberParam(StyleType.borderWidth, 1),
  loadMoreButtonCornerRadius: numberParam(StyleType.borderRadius, 1),
  layoutHeaderSidePadding: numberParam(StyleType.padding, 20),
  layoutHeaderTopPadding: numberParam(StyleType.paddingTop, 16),
  layoutHeaderBottomPadding: numberParam(StyleType.paddingBottom, 16),
  layoutImageSize: numberParam(StyleType.width, 80),
  itemsLayoutTopAndBottomPadding: numberParam(StyleType.padding, 16),
  dayLayoutTopAndBottomPadding: numberParam(StyleType.padding, 16),
  dayLayoutsidePadding: numberParam(StyleType.padding, 16),
  dayLayoutpaddingBetweenSessionsAndDates: numberParam(StyleType.gap, 40),
  sessionsItemsRegularButtonBorderWidth: numberParam(StyleType.borderWidth, 1),
  tableDayFont: fontParam('Body-M', { size: 16 }),
  loadMoreButtonFont: fontParam('Body-M', { size: 16 }),
  loadMoreButtonColor: colorParam(StyleType.color, 'color-fill-primary'),
  loadMoreButtonBackgroundAndOpacityColor: colorParam(
    StyleType.backgroundColor,
    'color-fill-background-primary',
  ),
  loadMoreButtonBorderAndOpacityColor: colorParam(
    StyleType.borderColor,
    'color-fill-primary',
  ),
  sessionItemsRegularButtonFont: fontParam('Body-M', { size: 16 }),
  sessionItemsRegularButtonColor: colorParamWithOpacity(
    StyleType.color,
    'color-fill-background-primary',
    1,
  ),
  sessionItemsPrimaryTextColor: colorParam(
    StyleType.color,
    'color-text-primary',
  ),
  sessionItemsServiceNameTextColor: colorParam(
    StyleType.color,
    'color-text-primary',
  ),
  sessionItemsSecondaryTextColor: colorParam(
    StyleType.color,
    'color-text-secondary',
  ),
  sessionsItemsRegularButtonBackgroundAndOpacityColor: colorParam(
    StyleType.backgroundColor,
    'color-text-primary',
  ),
  sessionsItemsRegularButtonBorderAndOpacityColor: colorParam(
    StyleType.borderColor,
    'color-text-primary',
  ),
  sessionsItemsRegularCornerRadius: numberParam(StyleType.borderRadius, 1),
  sessionsItemsTextColor: colorParam(
    StyleType.color,
    'color-fill-background-primary',
  ),
  sessionsItemsDisabledButtonBorderAndOpacityColor: colorParam(
    StyleType.borderColor,
    'button-color-fill-primary-disabled',
  ),
  sessionsItemsDisabledTextColor: colorParam(
    StyleType.color,
    'color-fill-background-primary',
  ),
  sessionsItemsButtonBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-text-primary',
  ),
  sessionsItemsDisabledButtonBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'button-color-fill-primary-disabled',
  ),
  sessionsItemsButtonBorderAndOpacityColor: colorParam(
    StyleType.borderColor,
    'color-text-primary',
  ),
  sessionsItemsSecondaryFont: fontParamWithMobileDefault('Body-M', 14, 16),
  sessionsItemsServiceNameFont: fontParam('Page-title', { size: 24 }),
  sessionsItemsPrimaryFont: fontParamWithMobileDefault('Body-M', 14, 16),
  tableDayColor: colorParam(StyleType.color, 'color-text-primary'),
  sessionItemsBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'button-color-fill-primary',
  ),
  sessionItemsImageOverlayOpacityAndColor: colorParam(
    StyleType.backgroundColor,
    'color-text-primary',
  ),
  sessionItemsCornerRadius: numberParam(StyleType.borderRadius, 0),
  sessionItemsImageCornerRadius: numberParam(StyleType.borderRadius, 0),
  tableDatesFont: fontParamWithMobileDefault('Body-M', 16, 20),
  tableDateColor: colorParam(StyleType.color, 'color-text-primary'),
  tableDayDividerOpacity: colorParamWithOpacity(
    StyleType.color,
    'color-text-primary',
    1,
  ),
  tableMobileDayDividerColor: colorParamWithOpacity(
    StyleType.color,
    'color-text-primary',
    0.06,
  ),
  generalBorderOpacityAndColor: colorParam(StyleType.borderColor, 'color-line'),
  tableSessionDividerOpacity: colorParamWithOpacity(
    StyleType.color,
    'color-line',
    0.3,
  ),
  generalWidgetBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-fill-background-primary',
  ),
  generalWidgetCornerRadius: numberParam(StyleType.borderRadius, 0),
  tableDayDividerWidth: numberParam(StyleType.width, 1),
  tableSessionDividerWidth: numberParam(StyleType.width, 1),
  headerTitleFont: fontParam('Page-title', { size: 24 }),
  headerTitleColor: colorParam(StyleType.color, 'color-text-primary'),
  datePickerDatesFont: fontParam('Body-M', { size: 14 }),
  datePickerDatesColor: colorParam(StyleType.color, 'color-text-primary'),
  selectionAndHighlightColor: colorParam(
    StyleType.backgroundColor,
    'color-text-primary',
  ),
  datePickerDisabledColor: colorParamWithOpacity(
    StyleType.color,
    'color-disabled',
    1,
  ),
  datePickerSelectedColor: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-fill-background-primary',
    100,
  ),
  filtersFont: fontParam('Body-M', { size: 14 }),
  filtersColor: colorParam(StyleType.color, 'color-text-secondary'),
  headerTitleVisibility: booleanParam(true),
  headerServiceFilterVisibility: booleanParam(true),
  headerStaffFilterVisibility: booleanParam(true),
  headerLocationFilterVisibility: booleanParam(true),
  headerTimezoneSelectionVisibility: booleanParam(false),
  tableDayDividerVisibility: booleanParam(true),
  tableSessionDividerVisibility: booleanParam(true),
  sessionServiceImageVisibility: booleanParam(true),
  sessionDurationVisibility: booleanParam(true),
  sessionStaffImageVisibility: booleanParam(false),
  sessionLocationVisibility: booleanParam(false),
  sessionStaffMemberVisibility: booleanParam(true),
  sessionPriceVisibility: booleanParam(false),
  sessionNumberOfSpotsLeftVisibility: booleanParam(true),
  canBookFromDailyAgenda: booleanParam(true),
};

const getItemsLayoutAlignment = (tpaData: any) => {
  if (tpaData.sessionItemsAlignment === AlignmentOptions.BOTTOM) {
    return 'flex-end';
  } else if (tpaData.sessionItemsAlignment === AlignmentOptions.CENTER) {
    return 'center';
  } else {
    return 'flex-start';
  }
};

const getDisplayStyle = (value: boolean) => {
  return value ? DisplayStyles.UNSET : DisplayStyles.NONE;
};

export const customCssVars: CustomCssVarsFn<ICustomStyleParamsInput> = ({
  styleParams,
  tpaData,
}): ICustomStylesParam => {
  return {
    agendaAlignedItem: getItemsLayoutAlignment(tpaData),
    displayHeaderTitle: getDisplayStyle(
      styleParams.booleans.headerTitleVisibility,
    ),
    displayHeaderServiceFilter: getDisplayStyle(
      styleParams.booleans.headerServiceFilterVisibility,
    ),
    displayHeaderStaffFilter: getDisplayStyle(
      styleParams.booleans.headerStaffFilterVisibility,
    ),
    displayHeaderLocationFilter: getDisplayStyle(
      styleParams.booleans.headerLocationFilterVisibility,
    ),
    displayHeaderTimezoneSelection: getDisplayStyle(
      styleParams.booleans.headerTimezoneSelectionVisibility,
    ),
    displaySessionServiceImage: getDisplayStyle(
      styleParams.booleans.sessionServiceImageVisibility,
    ),
    displaySessionDuration: getDisplayStyle(
      styleParams.booleans.sessionDurationVisibility,
    ),
    displaySessionStaffImage: getDisplayStyle(
      styleParams.booleans.sessionStaffImageVisibility,
    ),
    displaySessionLocation: getDisplayStyle(
      styleParams.booleans.sessionLocationVisibility,
    ),
    displaySessionStaffMember: getDisplayStyle(
      styleParams.booleans.sessionStaffMemberVisibility,
    ),
    displaySessionPrice: getDisplayStyle(
      styleParams.booleans.sessionPriceVisibility,
    ),
    displaySessionNumberOfSpotsLeft: getDisplayStyle(
      styleParams.booleans.sessionNumberOfSpotsLeftVisibility,
    ),
    displayTableDayDivider: getDisplayStyle(
      styleParams.booleans.tableDayDividerVisibility,
    ),
    displayTableSessionDivider: getDisplayStyle(
      styleParams.booleans.tableSessionDividerVisibility,
    ),
    displayBookFromDailyAgenda: getDisplayStyle(
      styleParams.booleans.canBookFromDailyAgenda,
    ),
  };
};

export default createStylesParams<IStylesParams>(styleParamsDefinitions);
